/* eslint jsx-a11y/label-has-associated-control: ["error", { "assert": "nesting" }] */
import { useState } from "react";
import { AffiliateOperation, AffiliateState } from "../../interfaces/affiliate";
import { affiliateError } from "../../hooks/api/admin/useAffiliates";
import { verifiedAffiliateEnvironment } from "../../hooks/api/admin/affiliateEnvironment";
import { isAffiliateTestEnvironment } from "../../utils/env";
import {
  affiliateSettings,
  launchPrepared,
  MAX_ALLOWLIST_WALLETS,
} from "./configuration";

export function ConfigurationPanel({
  data,
  locked,
  run,
}: {
  data?: AffiliateState;
  locked: boolean;
  run: (
    operation: AffiliateOperation,
    payload: Record<string, unknown>
  ) => Promise<boolean>;
}) {
  // Only explicitly edited fields override the latest verified server settings.
  const [draft, setDraft] = useState<{
    enabled?: boolean;
    weekly_cash?: boolean;
    allowlist?: string;
    enrollment_mode?: string;
    allowlist_limit?: string;
    aevo_usd_price?: string;
  }>({});
  const [error, setError] = useState<string>();
  const unavailable = locked || !verifiedAffiliateEnvironment(data);
  const enabled = draft.enabled ?? data?.enabled ?? false;
  const weeklyCash = draft.weekly_cash ?? data?.weekly_cash ?? false;
  const enrollmentMode = draft.enrollment_mode ?? data?.enrollment_mode ?? "";
  const prepared = launchPrepared(data);
  const edit = (change: Partial<typeof draft>) =>
    setDraft((value) => ({ ...value, ...change }));

  return (
    <section>
      <h2>1. Program access and payments</h2>
      <p>
        Choose who can participate. Keep affiliates and weekly cash payments
        disabled until preparation and launch checks are complete. Saving access
        preserves the program’s reward and fee policies.
      </p>
      {error && <p role="alert">{error}</p>}
      <form
        onSubmit={async (event) => {
          event.preventDefault();
          if (unavailable) return;
          setError(undefined);
          try {
            const current = affiliateSettings(data);
            const wallets =
              draft.allowlist === undefined
                ? current.allowlist
                : Array.from(
                    new Set(
                      draft.allowlist
                        .split(/[\s,]+/)
                        .filter(Boolean)
                        .map((wallet) => wallet.toLowerCase())
                    )
                  );
            if (wallets?.some((wallet) => !/^0x[0-9a-fA-F]{40}$/.test(wallet)))
              throw new Error(
                "Every allowlist entry must be an Ethereum wallet address."
              );
            if (enabled && enrollmentMode !== "open" && !wallets?.length)
              throw new Error(
                "Add at least one allowed wallet before enabling affiliates."
              );
            const limit =
              draft.allowlist_limit === undefined
                ? current.allowlist_limit
                : Number(draft.allowlist_limit);
            if (
              !Number.isInteger(limit) ||
              limit < 1 ||
              limit > MAX_ALLOWLIST_WALLETS ||
              (wallets?.length || 0) > limit
            )
              throw new Error(
                "Set an allowlist limit from 1 to 10000 that includes every listed wallet."
              );
            if (
              !prepared &&
              ((!current.enabled && enabled) ||
                (!current.weekly_cash && weeklyCash))
            )
              throw new Error(
                "Wait for the backend to confirm cash and points preparation before enabling affiliates or weekly payments."
              );
            if (
              !current.weekly_cash &&
              weeklyCash &&
              data?.capabilities?.weekly_cash !== true
            )
              throw new Error(
                "This backend has not enabled weekly cash payments."
              );
            if (
              (enrollmentMode === "open" || limit > 100) &&
              current.command_receipt_policy !== "expiring_v1"
            )
              throw new Error(
                "Complete cash and points preparation before expanding enrollment."
              );
            if (
              enrollmentMode === "open" &&
              current.waiver_policy !== "full_fill_or_none"
            )
              throw new Error(
                "Open enrollment requires the full-fill fee policy."
              );
            const price =
              draft.aevo_usd_price?.trim() || current.aevo_usd_price;
            if (!/^[0-9]+(\.[0-9]+)?$/.test(price))
              throw new Error("Enter a nonnegative decimal AEVO price.");
            if (
              await run("configure", {
                ...current,
                enabled,
                weekly_cash: weeklyCash,
                allowlist: wallets,
                enrollment_mode: enrollmentMode,
                allowlist_limit: limit,
                aevo_usd_price: price,
              })
            )
              setDraft({});
          } catch (caught) {
            setError(affiliateError(caught));
          }
        }}
      >
        <fieldset disabled={unavailable}>
          <label>
            Enrollment mode
            <select
              value={enrollmentMode}
              onChange={(event) =>
                edit({ enrollment_mode: event.target.value })
              }
            >
              {data?.enrollment_mode === "" && (
                <option value="">Existing allowlist mode</option>
              )}
              <option value="allowlist">Wallet allowlist</option>
              <option value="open">Open enrollment</option>
            </select>
          </label>
          <label>
            Allowed wallets
            <textarea
              value={draft.allowlist ?? (data?.allowlist || []).join("\n")}
              onChange={(event) => edit({ allowlist: event.target.value })}
              placeholder="One wallet per line"
            />
          </label>
          <label>
            Allowlist wallet limit
            <input
              type="number"
              min="1"
              max={MAX_ALLOWLIST_WALLETS}
              step="1"
              required
              value={draft.allowlist_limit ?? data?.allowlist_limit ?? ""}
              onChange={(event) =>
                edit({ allowlist_limit: event.target.value })
              }
            />
          </label>
          <label>
            Enable affiliates
            <input
              type="checkbox"
              checked={enabled}
              disabled={!prepared && !data?.enabled}
              onChange={(event) => edit({ enabled: event.target.checked })}
            />
          </label>
          <label>
            Enable weekly cash payments
            <input
              type="checkbox"
              checked={weeklyCash}
              disabled={
                !data?.weekly_cash &&
                (!prepared || data?.capabilities?.weekly_cash !== true)
              }
              onChange={(event) => edit({ weekly_cash: event.target.checked })}
            />
          </label>
          {isAffiliateTestEnvironment() && (
            <label>
              Test AEVO price (USD)
              <input
                inputMode="decimal"
                pattern="[0-9]+(\.[0-9]+)?"
                value={draft.aevo_usd_price ?? ""}
                placeholder={data?.aevo_usd_price || "Unchanged"}
                onChange={(event) =>
                  edit({ aevo_usd_price: event.target.value })
                }
              />
            </label>
          )}
          <button type="submit">Save program settings</button>
        </fieldset>
      </form>
      <p>
        Weekly cash payments release eligible USDC commissions after the hold
        period. Turning them off does not cancel payments already submitted.
      </p>
      {isAffiliateTestEnvironment() && (
        <p>
          The supplied AEVO price is a test value for historical obligations,
          not a market-price source.
        </p>
      )}
    </section>
  );
}
